// src/pages/Home/HomePage.tsx
// Modern, animated homepage — covers the full FileFusion platform

import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    FileText, FileDown, Combine, QrCode, Scan, Type, Image, FileJson,
    Database, Webhook, Mail, CreditCard,
    BookOpen, GraduationCap,
    Shield, Zap, Globe, HelpCircle,
    ArrowRight, LucideIcon
} from 'lucide-react';
import { m, AnimatePresence } from 'framer-motion';
import SEO from '../../components/SEO/SEO';
import { WebsiteSchema } from '../../components/Schema';
import { seoConfig } from '../../config/seo';
import { Typography } from '../../components/ui/typography';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ArticlePost {
    id: string | number;
    title: string;
    slug: string;
    category: string;
    date: string;
    readTime: number;
    excerpt: string;
}

// ── Platform Showcase Data ────────────────────────────────────────────────────

interface ShowcaseItem {
    icon: LucideIcon;
    title: string;
    blurb: string;
    link: string;
}

interface ShowcaseTab {
    id: string;
    label: string;
    accent: string;
    bg: string;
    items: ShowcaseItem[];
    seeAll: string;
    seeAllLabel: string;
}

const tabs: ShowcaseTab[] = [
    {
        id: 'tools',
        label: 'Tools',
        accent: '#1a73e8',
        bg: '#e8f0fe',
        seeAll: '/tools',
        seeAllLabel: 'Browse all tools →',
        items: [
            { icon: FileText, title: 'Text Compare', blurb: 'Side-by-side diff with character-level precision', link: '/tools/compare-text' },
            { icon: FileDown, title: 'File Reducer', blurb: 'Compress PDFs & images by up to 80%', link: '/tools/compress-pdf-images' },
            { icon: Combine, title: 'PDF Merge & Split', blurb: 'Merge or split PDFs with full page control', link: '/tools/merge-split-pdf' },
            { icon: QrCode, title: 'QR Generator', blurb: 'Custom QR codes with logo & colour support', link: '/tools/qr-code-generator' },
            { icon: Type, title: 'OCR Extractor', blurb: 'Extract text from images & scanned PDFs', link: '/tools/scan-image-to-text' },
            { icon: Image, title: 'BG Remover', blurb: 'AI-powered background removal in one click', link: '/tools/remove-background' },
            { icon: Scan, title: 'Barcode Scanner', blurb: 'Decode QR, EAN, UPC & Code 128 from images', link: '/tools/barcode-scanner' },
            { icon: FileJson, title: 'JSON Validator', blurb: 'Validate, format & query JSON with auto-fix', link: '/tools/json-validator-formatter' },
        ],
    },
    {
        id: 'devhub',
        label: 'Developer Hub',
        accent: '#1e8e3e',
        bg: '#e6f4ea',
        seeAll: '/dev-hub',
        seeAllLabel: 'Explore Dev Hub →',
        items: [
            { icon: Database, title: 'Mock API', blurb: 'Auto-generated REST API with chaos engineering params', link: '/dev-hub/mock-api' },
            { icon: Webhook, title: 'Webhook Tester', blurb: 'Live HTTP callback inspector with response rules', link: '/dev-hub/webhook-tester' },
            { icon: Mail, title: 'Email Sandbox', blurb: 'Black-hole SMTP catcher for dev & staging', link: '/dev-hub/email-sandbox' },
            { icon: CreditCard, title: 'Payment Simulator', blurb: 'Model payment lifecycles & fraud triggers safely', link: '/dev-hub/payment-simulator' },
        ],
    },
    {
        id: 'insights',
        label: 'Insights',
        accent: '#f9ab00',
        bg: '#fef7e0',
        seeAll: '/insights',
        seeAllLabel: 'Explore Insights →',
        items: [
            { icon: BookOpen, title: 'Engineering Guides', blurb: 'Deep-dive articles on diff algorithms, OCR, PDF & more', link: '/insights/articles' },
            { icon: GraduationCap, title: 'Professional Courses', blurb: 'Structured paths in Python, JS, HTML5 & C', link: '/insights/courses' },
        ],
    },
];

// ── Animated Counter ──────────────────────────────────────────────────────────

interface CounterProps {
    target: number | string;
    label: string;
    duration?: number;
}

const Counter: React.FC<CounterProps> = ({ target, label, duration = 1200 }) => {
    const [count, setCount] = useState(0);
    const ref = useRef<HTMLDivElement>(null);
    const started = useRef(false);
    const isNum = typeof target === 'number';

    useEffect(() => {
        const el = ref.current;
        if (!el || !isNum) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !started.current) {
                    started.current = true;
                    const start = performance.now();
                    const tick = (now: number) => {
                        const t = Math.min((now - start) / duration, 1);
                        const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
                        setCount(Math.round(ease * (target as number)));
                        if (t < 1) requestAnimationFrame(tick);
                    };
                    requestAnimationFrame(tick);
                }
            },
            { threshold: 0.5 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [target, duration, isNum]);

    return (
        <div ref={ref} className="flex flex-col items-center text-center">
            <div className="text-[3rem] sm:text-[3.5rem] font-light text-[#121317] leading-none mb-2">
                {isNum ? count : target}
            </div>
            <div className="text-[0.95rem] text-[#5f6368]">{label}</div>
        </div>
    );
};

// ── Article async load ────────────────────────────────────────────────────────

const getArticleData = () =>
    import('../Insights/Articles/ArticleData').then(m => ({ allPosts: m.allPosts || [] }));

// ── Page ──────────────────────────────────────────────────────────────────────

const HomePage: React.FC = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [featuredInsights, setFeaturedInsights] = useState<ArticlePost[]>([]);

    // Auto-cycle tabs
    useEffect(() => {
        const interval = setInterval(() => {
            setActiveTab(prev => (prev + 1) % tabs.length);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    // Load articles
    useEffect(() => {
        let mounted = true;
        const timer = setTimeout(() => {
            if (mounted) {
                getArticleData().then(data => {
                    if (mounted) {
                        const sorted = [...data.allPosts].sort(
                            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
                        );
                        setFeaturedInsights(sorted.slice(0, 3));
                    }
                });
            }
        }, 800);
        return () => { mounted = false; clearTimeout(timer); };
    }, []);

    const currentTab = tabs[activeTab];

    const generateHomeSchema = () => ({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'FileFusion',
        url: 'https://filefusion.app',
        description: 'Free online tools for file management, text comparison, QR codes, OCR, and more.',
    });

    return (
        <>
            <SEO {...seoConfig.home} schema={generateHomeSchema()} />
            <WebsiteSchema />

            <div className="min-h-screen bg-white font-brand text-[#202124]">

                {/* ── Hero ────────────────────────────────────────────────────── */}
                <header className="relative overflow-hidden">

                    {/* Hero content */}
                    <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 text-center">
                        <m.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease: 'easeOut' }}
                        >
                            <Typography variant="hero-title" as="h1" weight="light" className="mb-6 text-[#121317]">
                                Professional tools.{' '}
                                <span className="text-[#1a73e8]">Developer services.</span>{' '}
                                Engineering knowledge.
                            </Typography>
                            <Typography variant="hero-subtitle" weight="normal" className="max-w-2xl mx-auto text-[#5f6368] mb-10">
                                A complete platform built for practitioners. Every tool, service, and course is
                                free, private, and available without sign-up.
                            </Typography>
                        </m.div>

                        <m.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
                            className="flex flex-wrap gap-4 justify-center"
                        >
                            <Link
                                to="/tools"
                                className="inline-flex items-center gap-2 px-7 py-3 bg-[#1a73e8] text-white font-medium rounded-full no-underline hover:bg-[#1557b0] transition-colors shadow-sm"
                            >
                                Explore Tools
                                <ArrowRight size={18} />
                            </Link>
                            <Link
                                to="/dev-hub"
                                className="inline-flex items-center gap-2 px-7 py-3 bg-white text-[#3c4043] font-medium rounded-full border border-[#dadce0] no-underline hover:bg-[#f8f9fa] transition-colors"
                            >
                                Visit Developer Hub
                            </Link>
                        </m.div>

                        {/* Trust micro-badges */}
                        <m.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.5 }}
                            className="flex flex-wrap gap-6 justify-center mt-10 text-[0.85rem] text-[#5f6368]"
                        >
                            {['100% Free', 'No Sign-up', 'Files stay on your device'].map(t => (
                                <span key={t} className="flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#1e8e3e] inline-block" />
                                    {t}
                                </span>
                            ))}
                        </m.div>
                    </div>
                </header>

                {/* ── Platform Showcase Tabs ───────────────────────────────────── */}
                <section className="max-w-7xl mx-auto px-6 py-16 border-t border-[#f1f3f4]">
                    {/* Tab strip */}
                    <div className="flex items-center gap-2 mb-10 flex-wrap">
                        {tabs.map((tab, i) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(i)}
                                className={`px-5 py-2 rounded-full font-medium text-[0.95rem] transition-all duration-200 border ${i === activeTab
                                    ? 'text-white border-transparent shadow-sm'
                                    : 'bg-white text-[#5f6368] border-[#dadce0] hover:bg-[#f8f9fa]'
                                    }`}
                                style={i === activeTab ? { backgroundColor: tab.accent, borderColor: tab.accent } : {}}
                            >
                                {tab.label}
                            </button>
                        ))}
                        {/* Progress bar */}
                        <div className="ml-auto hidden sm:flex items-center gap-2">
                            {tabs.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveTab(i)}
                                    className={`h-1.5 rounded-full transition-all duration-300 ${i === activeTab ? 'w-8' : 'w-2 bg-[#dadce0]'}`}
                                    style={i === activeTab ? { backgroundColor: currentTab.accent } : {}}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Tab content */}
                    <AnimatePresence mode="wait">
                        <m.div
                            key={currentTab.id}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.3, ease: 'easeOut' }}
                        >
                            <div className={`rounded-[32px] p-8 md:p-12`} style={{ backgroundColor: currentTab.bg + '55' }}>
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
                                    {currentTab.items.map((item) => {
                                        const Icon = item.icon;
                                        return (
                                            <Link
                                                key={item.title}
                                                to={item.link}
                                                className="group bg-white rounded-2xl p-5 flex flex-col gap-3 no-underline border border-white hover:border-[#dadce0] hover:shadow-md transition-all duration-200"
                                            >
                                                <div
                                                    className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-200"
                                                    style={{ backgroundColor: currentTab.bg, color: currentTab.accent }}
                                                >
                                                    <Icon size={20} strokeWidth={1.5} />
                                                </div>
                                                <div>
                                                    <div className="font-medium text-[#121317] text-[0.95rem] mb-1 group-hover:text-[#1a73e8] transition-colors">
                                                        {item.title}
                                                    </div>
                                                    <div className="text-[0.8rem] text-[#5f6368] leading-snug">
                                                        {item.blurb}
                                                    </div>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                                <Link
                                    to={currentTab.seeAll}
                                    className="inline-flex items-center font-medium text-[0.95rem] no-underline hover:underline transition-colors"
                                    style={{ color: currentTab.accent }}
                                >
                                    {currentTab.seeAllLabel}
                                </Link>
                            </div>
                        </m.div>
                    </AnimatePresence>
                </section>

                {/* ── Animated Counter Strip ───────────────────────────────────── */}
                <section className="max-w-7xl mx-auto px-6 py-16 border-t border-[#f1f3f4]">
                    <div className="flex justify-center gap-16 sm:gap-28 flex-wrap">
                        <Counter target={8} label="Professional Tools" />
                        <Counter target={4} label="Developer Services" />
                        <Counter target="100%" label="Free Forever" />
                    </div>
                </section>

                {/* ── Platform Qualities ───────────────────────────────────────── */}
                <section className="max-w-7xl mx-auto px-6 py-16 border-t border-[#f1f3f4]">
                    <div className="bg-[#f8f9fa] rounded-[32px] p-8 md:p-16 grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                        {[
                            {
                                icon: Shield,
                                color: '#1a73e8',
                                bg: '#e8f0fe',
                                title: 'Privacy by Design',
                                desc: 'Files processed with encryption and deleted immediately. Browser-based tools never upload your data at all. Your documents are yours alone.',
                            },
                            {
                                icon: Zap,
                                color: '#1e8e3e',
                                bg: '#e6f4ea',
                                title: 'Instant Results',
                                desc: 'Optimised algorithms for large files, chunked processing for huge inputs, hardware acceleration where supported. Seconds, not minutes.',
                            },
                            {
                                icon: Globe,
                                color: '#f9ab00',
                                bg: '#fef7e0',
                                title: 'Works Everywhere',
                                desc: 'Every modern browser on Windows, Mac, Linux, iOS, and Android. No installation, no registration, no regional restrictions.',
                            },
                        ].map((q, i) => {
                            const Icon = q.icon;
                            return (
                                <m.div
                                    key={i}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: i * 0.1 }}
                                    className="flex flex-col items-center"
                                >
                                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 shadow-sm" style={{ backgroundColor: q.bg }}>
                                        <Icon size={26} style={{ color: q.color }} />
                                    </div>
                                    <h3 className="text-xl font-medium text-[#121317] mb-3">{q.title}</h3>
                                    <p className="text-[#5f6368] leading-relaxed max-w-xs">{q.desc}</p>
                                </m.div>
                            );
                        })}
                    </div>
                </section>

                {/* ── Insights Teaser ──────────────────────────────────────────── */}
                {featuredInsights.length > 0 && (
                    <section className="max-w-7xl mx-auto px-6 py-16 border-t border-[#f1f3f4]">
                        <div className="flex justify-between items-baseline mb-12 gap-4">
                            <div>
                                <h2 className="text-3xl font-light text-[#121317] mb-2">Latest Insights</h2>
                                <p className="text-[#5f6368]">Engineering articles written by practising developers</p>
                            </div>
                            <Link to="/insights" className="text-[#1a73e8] font-medium hover:underline shrink-0 flex items-center gap-1 group">
                                Browse all
                                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {featuredInsights.map((post, i) => (
                                <m.article
                                    key={post.id}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: i * 0.07 }}
                                    className="bg-white border border-[#dadce0] rounded-[32px] p-7 hover:shadow-md transition-all duration-200"
                                >
                                    <span className="inline-block text-[0.72rem] font-medium uppercase tracking-wider mb-4 bg-[#e8f0fe] py-1 px-3 rounded-full text-[#1a73e8]">
                                        {post.category?.replace(/-/g, ' ')}
                                    </span>
                                    <h3 className="text-[1.05rem] font-medium text-[#121317] mb-3 leading-snug">
                                        <Link to={`/insights/articles/${post.slug}`} className="no-underline hover:text-[#1a73e8] transition-colors">
                                            {post.title}
                                        </Link>
                                    </h3>
                                    <p className="text-[0.875rem] text-[#5f6368] leading-relaxed line-clamp-3">{post.excerpt}</p>
                                    <div className="mt-4 text-[0.8rem] text-[#80868b]">{post.readTime} min read</div>
                                </m.article>
                            ))}
                        </div>
                    </section>
                )}

                {/* ── FAQ / Help CTA ───────────────────────────────────────────── */}
                <section className="max-w-7xl mx-auto px-6 py-16 border-t border-[#f1f3f4]">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-12 bg-[#f8f9fa] rounded-[32px] p-8 md:p-16">
                        <div className="max-w-xl text-center md:text-left">
                            <div className="w-12 h-12 bg-[#e8f0fe] rounded-2xl flex items-center justify-center text-[#1a73e8] mb-6 mx-auto md:mx-0">
                                <HelpCircle size={24} />
                            </div>
                            <h2 className="text-3xl font-light text-[#121317] mb-4">
                                Questions about FileFusion?
                            </h2>
                            <p className="text-[#5f6368] text-lg leading-relaxed">
                                Find answers about privacy, security, file limits, and browser
                                compatibility in our help centre.
                            </p>
                        </div>
                        <div className="flex flex-col items-center gap-4 shrink-0">
                            <Link
                                to="/faq"
                                className="flex items-center gap-2 px-6 py-2.5 bg-[#1a73e8] text-white rounded-full font-medium hover:bg-[#1557b0] transition-colors shadow-sm no-underline"
                            >
                                Browse FAQs
                            </Link>
                            <div className="flex flex-wrap gap-3 justify-center">
                                {[
                                    { to: '/features', label: 'All Features' },
                                    { to: '/tools', label: 'All Tools' },
                                    { to: '/dev-hub', label: 'Developer Hub' },
                                ].map(l => (
                                    <Link
                                        key={l.to}
                                        to={l.to}
                                        className="text-sm font-medium text-[#5f6368] hover:text-[#121317] px-4 py-2 bg-white rounded-full border border-[#dadce0] transition-colors no-underline"
                                    >
                                        {l.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

            </div>
        </>
    );
};

export default HomePage;
